import { Transporter } from './transporter.model';
import { OfferMerchandiseTypeEnum } from './enums/offer-merchandise-type.enum';

export class Vehicle {
  public id!: string;
  public transporter!: Transporter;
  public category!: VehicleCategory;
  public type!: VehicleType;
  public length?: number;
  public width?: number;
  public height?: number;
  public payloadValue!: number;
  public surface?: number;
  public volume?: number;
  public createdDate!: Date;
  public updatedDate!: Date;
  public usedInOffers: boolean = false; //@Transient

  constructor(data: any = {}) {
    Object.assign(this, data);
    this.category = LIST_VEHICLE_CATEGORY[data.category || VehicleCategoryEnum.RIGID_LORRY];
    this.type = LIST_VEHICLE_TYPE[data.type || VehicleTypeEnum.TAUTLINER];
    this.transporter = new Transporter(data.transporter);
  }

  public getSurface(): number | null {
    if (this.surface) return this.surface;
    if (this?.length && this?.width) return this.length * this.width;
    return null;
  }

  public getVolume(): number | null {
    if (this.volume) return this.volume;
    if (this.length && this.width && this.height) return this.length * this.width * this.height;
    return null;
  }

  serialize(): any {
    return {
      id: this.id,
      transporter: { id: this.transporter.id },
      category: this.category.code,
      type: this.type?.code,
      length: this.length,
      width: this.width,
      height: this.height,
      payloadValue: this.payloadValue,
      surface: this.surface,
      volume: this.volume,
      createdDate: this.createdDate,
      updatedDate: this.updatedDate,
    };
  }
}

export enum VehicleCategoryEnum {
  LIGHT_VEHICLE = 'LIGHT_VEHICLE',
  TRUCK_WITH_TRAILER = 'TRUCK_WITH_TRAILER',
  RIGID_LORRY = 'RIGID_LORRY',
  ARTICULATED_UNIT = 'ARTICULATED_UNIT',
}

export class VehicleCategory {
  public code!: string;
  public labelKey!: string;

  constructor(data: any = {}) {
    Object.assign(this, data);
  }
}

export const LIST_VEHICLE_CATEGORY: { [key: string]: VehicleCategory } = {
  LIGHT_VEHICLE: new VehicleCategory({ code: VehicleCategoryEnum.LIGHT_VEHICLE, labelKey: 'VEHICLE.CATEGORY.LIGHT_VEHICLE' }),
  TRUCK_WITH_TRAILER: new VehicleCategory({ code: VehicleCategoryEnum.TRUCK_WITH_TRAILER, labelKey: 'VEHICLE.CATEGORY.TRUCK_WITH_TRAILER' }),
  RIGID_LORRY: new VehicleCategory({ code: VehicleCategoryEnum.RIGID_LORRY, labelKey: 'VEHICLE.CATEGORY.RIGID_LORRY' }),
  ARTICULATED_UNIT: new VehicleCategory({ code: VehicleCategoryEnum.ARTICULATED_UNIT, labelKey: 'VEHICLE.CATEGORY.ARTICULATED_UNIT' }),
};

export class MeasureUnit {
  public value!: string;
  public label!: string;

  constructor(data: any = {}) {
    Object.assign(this, data);
  }
}

export enum MeasureUnitEnum {
  KG = 'KG',
  TONNE = 'TONNE',
  METER = 'METER',
  LITER = 'LITER',
  CENTIMETER = 'CENTIMETER',
  SQUARE_METER = 'SQUARE_METER',
  CUBIC_METER = 'CUBIC_METER',
  CELSIUS = 'CELSIUS',
  KG_CUBIC_METER = 'KG_CUBIC_METER',
}

export const LIST_MEASURE_UNIT: { [key: string]: MeasureUnit } = {
  KG: new MeasureUnit({ value: MeasureUnitEnum.KG, label: 'kg' }),
  TONNE: new MeasureUnit({ value: MeasureUnitEnum.TONNE, label: 'T' }),
  LITER: new MeasureUnit({ value: MeasureUnitEnum.LITER, label: 'L' }),
  METER: new MeasureUnit({ value: MeasureUnitEnum.METER, label: 'm' }),
  CENTIMETER: new MeasureUnit({ value: MeasureUnitEnum.CENTIMETER, label: 'cm' }),
  SQUARE_METER: new MeasureUnit({ value: MeasureUnitEnum.SQUARE_METER, label: 'm²' }),
  CUBIC_METER: new MeasureUnit({ value: MeasureUnitEnum.CUBIC_METER, label: 'm³' }),
  KG_CUBIC_METER: new MeasureUnit({ value: MeasureUnitEnum.KG_CUBIC_METER, label: 'kg/m³' }),
  CELSIUS: new MeasureUnit({ value: MeasureUnitEnum.CELSIUS, label: '°C' }),
};

export enum VehicleTypeEnum {
  TAUTLINER = 'TAUTLINER',
  SAVOYARDE = 'SAVOYARDE',
  STANDARD_VAN = 'STANDARD_VAN',
  LARGE_VOLUME = 'LARGE_VOLUME',
  DOUBLE_DECK = 'DOUBLE_DECK',
  CITYLINER = 'CITYLINER',
  FLATBED = 'FLATBED',
  CONTAINER_CARRIER = 'CONTAINER_CARRIER',
  SIMPLE_ISOTHERMAL = 'SIMPLE_ISOTHERMAL',
  MONO_TEMPERATURE_REEFER = 'MONO_TEMPERATURE_REEFER',
  BI_TEMPERATURE_REEFER = 'BI_TEMPERATURE_REEFER',
  MULTI_TEMPERATURE_REEFER = 'MULTI_TEMPERATURE_REEFER',
  HYDROCARBONS_CISTERN = 'HYDROCARBONS_CISTERN',
  CHEMICAL_CISTERN = 'CHEMICAL_CISTERN',
  FOOD_CISTERN = 'FOOD_CISTERN',
  BITUMEN_CISTERN = 'BITUMEN_CISTERN',
  CRYOGENIC_CISTERN = 'CRYOGENIC_CISTERN',
  PUBLIC_WORKS_CONTAINER = 'PUBLIC_WORKS_CONTAINER',
  CEREAL_CONTAINER = 'CEREAL_CONTAINER',
  POWDERY_CONTAINER = 'POWDERY_CONTAINER',
  EMPTY_STAGE = 'EMPTY_STAGE',
  CARRIER_LOADER = 'CARRIER_LOADER',
  DRINK_HOLDER = 'DRINK_HOLDER',
  TIMBER_TRAILER = 'TIMBER_TRAILER',
}

export class VehicleType {
  public code!: string;
  public labelKey!: string;
  public merchandiseType!: OfferMerchandiseTypeEnum;
  public isControlledTemperature?: boolean;

  constructor(data: any = {}) {
    Object.assign(this, data);
  }
}

export const LIST_VEHICLE_TYPE: { [key: string]: VehicleType } = {
  TAUTLINER: new VehicleType({
    code: VehicleTypeEnum.TAUTLINER,
    labelKey: 'VEHICLE.TYPE.TAUTLINER',
    merchandiseType: OfferMerchandiseTypeEnum.CONDITIONED,
    isControlledTemperature: false,
  }),
  SAVOYARDE: new VehicleType({
    code: VehicleTypeEnum.SAVOYARDE,
    labelKey: 'VEHICLE.TYPE.SAVOYARDE',
    merchandiseType: OfferMerchandiseTypeEnum.CONDITIONED,
    isControlledTemperature: false,
  }),
  STANDARD_VAN: new VehicleType({
    code: VehicleTypeEnum.STANDARD_VAN,
    labelKey: 'VEHICLE.TYPE.STANDARD_VAN',
    merchandiseType: OfferMerchandiseTypeEnum.CONDITIONED,
    isControlledTemperature: false,
  }),
  LARGE_VOLUME: new VehicleType({
    code: VehicleTypeEnum.LARGE_VOLUME,
    labelKey: 'VEHICLE.TYPE.LARGE_VOLUME',
    merchandiseType: OfferMerchandiseTypeEnum.CONDITIONED,
    isControlledTemperature: false,
  }),
  DOUBLE_DECK: new VehicleType({
    code: VehicleTypeEnum.DOUBLE_DECK,
    labelKey: 'VEHICLE.TYPE.DOUBLE_DECK',
    merchandiseType: OfferMerchandiseTypeEnum.CONDITIONED,
    isControlledTemperature: false,
  }),
  CITYLINER: new VehicleType({
    code: VehicleTypeEnum.CITYLINER,
    labelKey: 'VEHICLE.TYPE.CITYLINER',
    merchandiseType: OfferMerchandiseTypeEnum.CONDITIONED,
    isControlledTemperature: false,
  }),
  FLATBED: new VehicleType({
    code: VehicleTypeEnum.FLATBED,
    labelKey: 'VEHICLE.TYPE.FLATBED',
    merchandiseType: OfferMerchandiseTypeEnum.CONDITIONED,
    isControlledTemperature: false,
  }),
  CONTAINER_CARRIER: new VehicleType({
    code: VehicleTypeEnum.CONTAINER_CARRIER,
    labelKey: 'VEHICLE.TYPE.CONTAINER_CARRIER',
    merchandiseType: OfferMerchandiseTypeEnum.CONDITIONED,
    isControlledTemperature: false,
  }),
  SIMPLE_ISOTHERMAL: new VehicleType({
    code: VehicleTypeEnum.SIMPLE_ISOTHERMAL,
    labelKey: 'VEHICLE.TYPE.SIMPLE_ISOTHERMAL',
    merchandiseType: OfferMerchandiseTypeEnum.CONDITIONED,
    isControlledTemperature: true,
  }),
  MONO_TEMPERATURE_REEFER: new VehicleType({
    code: VehicleTypeEnum.MONO_TEMPERATURE_REEFER,
    labelKey: 'VEHICLE.TYPE.MONO_TEMPERATURE_REEFER',
    merchandiseType: OfferMerchandiseTypeEnum.CONDITIONED,
    isControlledTemperature: true,
  }),
  BI_TEMPERATURE_REEFER: new VehicleType({
    code: VehicleTypeEnum.BI_TEMPERATURE_REEFER,
    labelKey: 'VEHICLE.TYPE.BI_TEMPERATURE_REEFER',
    merchandiseType: OfferMerchandiseTypeEnum.CONDITIONED,
    isControlledTemperature: true,
  }),
  MULTI_TEMPERATURE_REEFER: new VehicleType({
    code: VehicleTypeEnum.MULTI_TEMPERATURE_REEFER,
    labelKey: 'VEHICLE.TYPE.MULTI_TEMPERATURE_REEFER',
    merchandiseType: OfferMerchandiseTypeEnum.CONDITIONED,
    isControlledTemperature: true,
  }),
  HYDROCARBONS_CISTERN: new VehicleType({
    code: VehicleTypeEnum.HYDROCARBONS_CISTERN,
    labelKey: 'VEHICLE.TYPE.HYDROCARBONS_CISTERN',
    merchandiseType: OfferMerchandiseTypeEnum.UNCONDITIONED,
  }),
  CHEMICAL_CISTERN: new VehicleType({ code: VehicleTypeEnum.CHEMICAL_CISTERN, labelKey: 'VEHICLE.TYPE.CHEMICAL_CISTERN', merchandiseType: OfferMerchandiseTypeEnum.UNCONDITIONED }),
  FOOD_CISTERN: new VehicleType({ code: VehicleTypeEnum.FOOD_CISTERN, labelKey: 'VEHICLE.TYPE.FOOD_CISTERN', merchandiseType: OfferMerchandiseTypeEnum.UNCONDITIONED }),
  BITUMEN_CISTERN: new VehicleType({ code: VehicleTypeEnum.BITUMEN_CISTERN, labelKey: 'VEHICLE.TYPE.BITUMEN_CISTERN', merchandiseType: OfferMerchandiseTypeEnum.UNCONDITIONED }),
  CRYOGENIC_CISTERN: new VehicleType({
    code: VehicleTypeEnum.CRYOGENIC_CISTERN,
    labelKey: 'VEHICLE.TYPE.CRYOGENIC_CISTERN',
    merchandiseType: OfferMerchandiseTypeEnum.UNCONDITIONED,
  }),
  PUBLIC_WORKS_CONTAINER: new VehicleType({
    code: VehicleTypeEnum.PUBLIC_WORKS_CONTAINER,
    labelKey: 'VEHICLE.TYPE.PUBLIC_WORKS_CONTAINER',
    merchandiseType: OfferMerchandiseTypeEnum.UNCONDITIONED,
  }),
  CEREAL_CONTAINER: new VehicleType({ code: VehicleTypeEnum.CEREAL_CONTAINER, labelKey: 'VEHICLE.TYPE.CEREAL_CONTAINER', merchandiseType: OfferMerchandiseTypeEnum.UNCONDITIONED }),
  POWDERY_CONTAINER: new VehicleType({
    code: VehicleTypeEnum.POWDERY_CONTAINER,
    labelKey: 'VEHICLE.TYPE.POWDERY_CONTAINER',
    merchandiseType: OfferMerchandiseTypeEnum.UNCONDITIONED,
  }),
  EMPTY_STAGE: new VehicleType({ code: VehicleTypeEnum.EMPTY_STAGE, labelKey: 'VEHICLE.TYPE.EMPTY_STAGE', merchandiseType: OfferMerchandiseTypeEnum.UNCONDITIONED }),
  CARRIER_LOADER: new VehicleType({ code: VehicleTypeEnum.CARRIER_LOADER, labelKey: 'VEHICLE.TYPE.CARRIER_LOADER', merchandiseType: OfferMerchandiseTypeEnum.UNCONDITIONED }),
  DRINK_HOLDER: new VehicleType({ code: VehicleTypeEnum.DRINK_HOLDER, labelKey: 'VEHICLE.TYPE.DRINK_HOLDER', merchandiseType: OfferMerchandiseTypeEnum.UNCONDITIONED }),
  TIMBER_TRAILER: new VehicleType({ code: VehicleTypeEnum.TIMBER_TRAILER, labelKey: 'VEHICLE.TYPE.TIMBER_TRAILER', merchandiseType: OfferMerchandiseTypeEnum.UNCONDITIONED }),
};
